// routes/_app.$usernameRaw.likes.($subTab).tsx
import { getI18n } from 'src/i18n/i18n.middleware'
import { userProfileHandle, userProfileLoader } from 'src/utils/router/route'
import { dynamic } from 'src/utils/ssr'
import type { SeoServerConfig } from 'src/utils/ssr-meta/consul.server'
import {
  DEFAULT_TITLE,
  profileMeta,
  profileMetaLoader,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.$usernameRaw.likes.($subTab)'

const getClientModule = () =>
  import('src/clientRoutes/_app.$usernameRaw.likes.($subTab)')

export const handle = userProfileHandle

/**
 * SSR loader for user likes page.
 * Same behavior as /@username for meta tags.
 */
export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { bucketString, parseUsername } =
    await import('src/utils/ssr-meta/url.server')
  const { fetchUserForSSR } =
    await import('src/utils/ssr-meta/graphql/user.server')
  const { getSeoConfig } = await import('src/utils/ssr-meta/consul.server')

  /**
   * Determine if a user profile should be noindexed based on SEO rollout rules.
   */
  function shouldNoIndexUser(
    username: string,
    likesReceived: number,
    config: Required<SeoServerConfig>,
  ): boolean {
    const { gradualRolloutPercent, userIndexedMinimalLikes } = config
    const inRolloutBucket = bucketString(username, 100) < gradualRolloutPercent

    return inRolloutBucket && likesReceived < userIndexedMinimalLikes
  }

  const usernameRaw = params.usernameRaw
  if (!usernameRaw) return profileMetaLoader({ requestUrl: request.url })

  const username = parseUsername(usernameRaw)
  if (!username) return profileMetaLoader({ requestUrl: request.url })

  const [user, seoConfig] = await Promise.all([
    fetchUserForSSR(username),
    getSeoConfig(),
  ])

  if (!user) return profileMetaLoader({ requestUrl: request.url })

  const likesReceived = user.artworkLikedCount ?? 0
  const shouldNoIndex = shouldNoIndexUser(username, likesReceived, seoConfig)

  const { t } = getI18n(context)
  const i18nTitle = user.displayName
    ? t('common:meta.title.user.home', { username: user.displayName })
    : DEFAULT_TITLE

  let avatarUrl = user.avatarSmallThumbnailMediaUrl || undefined
  if (avatarUrl) {
    avatarUrl = avatarUrl.replace('/images/avatar/', '/images/stillThumb/')
  }

  return {
    ...profileMetaLoader({
      requestUrl: request.url,
      title: i18nTitle || DEFAULT_TITLE,
      image: avatarUrl,
      twitterCard: avatarUrl ? 'summary' : 'summary_large_image',
    }),
    user,
    noIndex: shouldNoIndex,
  }
}

/** Meta function for SSR SEO. Same as /@username. */
export function meta({ loaderData }: Route.MetaArgs) {
  // profileMeta handles user-not-found and shouldNoIndex cases internally
  return profileMeta(loaderData)
}

export async function clientLoader(args: Route.ClientLoaderArgs) {
  await userProfileLoader({
    ...args,
    params: { ...args.params, tab: 'likes' },
  })
  return args.serverLoader?.()
}
clientLoader.hydrate = true

export default dynamic(getClientModule, { ssr: false })
